import { background, border, focus, icon, text } from "@air/design-tokens";

// Compute `group/variable` for every (group, variable) pair across the
// 5 semantic color groups. TypeScript evaluates this at compile time;
// the resulting union has one literal entry per valid color token.
type Join<K, P> = K extends string
  ? P extends string
    ? `${K}/${P}`
    : never
  : never;

type GroupTokens<G> = G extends {
  name: infer N extends string;
  variables: infer V;
}
  ? V extends Record<infer K, unknown>
    ? K extends string
      ? Join<N, K>
      : never
    : never
  : never;

export type IconColor =
  | GroupTokens<typeof text>
  | GroupTokens<typeof background>
  | GroupTokens<typeof border>
  | GroupTokens<typeof icon>
  | GroupTokens<typeof focus>;

export const DEFAULT_ICON_COLOR: IconColor = "icon/default";

const SEMANTIC_COLOR_GROUPS = [text, background, border, icon, focus] as const;

const SEMANTIC_ICON_COLORS: ReadonlySet<string> = new Set(
  SEMANTIC_COLOR_GROUPS.flatMap((group) =>
    Object.keys(group.variables).map((variable) => `${group.name}/${variable}`),
  ),
);

// Resolves a `group/variable` path to its emitted CSS variable name.
// `toCssVariables` in `@air/design-tokens` emits `--air-{group}-{variable}`
// with a hyphen separator, so we translate the `/` to `-` here.
export function tokenToCssVariableName(path: IconColor): string {
  return `--air-${path.replace("/", "-")}`;
}

export function isSemanticIconColor(color: string): color is IconColor {
  return SEMANTIC_ICON_COLORS.has(color);
}

// Resolves a `color` prop into the value applied to the SVG root. Slash-
// form tokens (`icon/emphasis`, `text/primary`) become the matching
// `var(--air-...)` reference; any other string (raw hex, design-token
// expression, computed `ColorValue`) passes through unchanged. The
// distinction is purely on the input — `unstyled` does not affect which
// branch is taken, only whether the result is forwarded.
export function resolveIconColor(color: string): string {
  return isSemanticIconColor(color)
    ? `var(${tokenToCssVariableName(color)})`
    : color;
}

// Mirror of IconColor for the Storybook catalog, generated at runtime so
// adding a semantic token drops a new option into the dropdown without
// other code changes.
export const COLOR_OPTIONS_BY_GROUP: ReadonlyArray<{
  group: string;
  options: ReadonlyArray<string>;
}> = SEMANTIC_COLOR_GROUPS.map((group) => ({
  group: group.name,
  options: Object.keys(group.variables).map((key) => `${group.name}/${key}`),
}));
